import chunk from "lodash-es/chunk";
import cloneDeep from "lodash-es/cloneDeep";
import flatten from "lodash-es/flatten";
import Subscriptions from "../../Subscriptions";
import {bindAllClassMethods} from "../../utils/utils";
import Services from "../Services";
import {
  addDispositionFromCache,
  getDispositions as fetchDispositions,
  processDispositionData,
  observableKey,
  mergeObservables
} from "./services";

const cacheKey = "ats-security-disposition-cache";
const timestampKey = "ats-security-disposition-cache-updated";
const updateKey = "disposition-cache-update";
const chunkSize = 25;
const cacheTimeout = 1 * 3600 * 1000; // 1 hour
const reconcileInterval = 3 * 1000; // 3 seconds
const maxConnections = 4;

const subscriptionKeys = {
  update: updateKey
};

const dispositionStatus = {
  COMPLETED: "completed",
  FAILED: "failed",
  PENDING: "pending"
};

class DispositionCache {
  constructor() {
    this.cache = this.loadCache();

    this.queue = {};
    this.pending = {};

    this.subscriptionKeys = subscriptionKeys;
    this.updateKey = updateKey;
    this.observableKey = observableKey;
    this.currentConnections = 0;
    this.dispositionStatus = dispositionStatus;

    bindAllClassMethods(this);

    setInterval(this.reconcileQueue, reconcileInterval);
  }

  loadCache(save = true) {
    let cache = this.cache || {},
      now = Date.now();

    if (window.localStorage) {
      const timestamp = window.localStorage.getItem(timestampKey),
        cacheTime = parseInt(timestamp);

      if (this.cacheTime !== cacheTime) {
        const cacheData = window.localStorage.getItem(cacheKey) || [];

        try {
          if (cacheData) {
            cache = JSON.parse(cacheData);
            this.cacheTime = cacheTime;
          }
        } catch (e) {
          // no-op
        }
      }
    }

    // Clean the cache of anything too old.
    let needsSave = false;
    Object.keys(cache).forEach(key => {
      const split = key.split(":"),
        type = split.shift(),
        value = split.join(),
        observable = {type, value, ...cache[key]};

      if (!observable.timestamp || !observable.status) {
        delete cache[observableKey(observable)];
        needsSave = true;
      }

      const diff = now - observable.timestamp;
      if (diff > cacheTimeout) {
        delete cache[key];
        needsSave = true;
      }
    });

    this.cache = cache;

    if (save && needsSave) {
      this.saveCache(cache);
    }

    return cache;
  }

  // Asynchronous
  getDispositions(observables, cacheOnly = false, fromQueue = false) {
    const _this = this,
      _observables = Array.isArray(observables) ? observables : [observables],
      toFetch = [],
      noFetch = [];

    // If another tab has updated the cache, use it.
    this.loadCache();

    _observables.forEach(obs => {
      const key = observableKey(obs),
        result = this.cache[key],
        {status} = result || {};

      if (
        (!result || status === dispositionStatus.FAILED) &&
        !cacheOnly &&
        !this.pending[key]
      ) {
        toFetch.push(obs);
      } else {
        noFetch.push(obs);
        delete this.queue[key];
      }
    });

    const promises = [],
      dispositionPromises = [],
      observableChunks = chunk(toFetch, chunkSize);

    for (const observableChunk of observableChunks) {
      if (fromQueue) {
        if (_this.currentConnections >= maxConnections) {
          break;
        }

        _this.currentConnections += 1;
      }

      // Manage limits and pending
      observableChunk.forEach(obs => {
        const key = observableKey(obs);

        delete this.queue[key];
      });

      this.addPendingObservables(observableChunk);

      const clonedObservableChunk = cloneDeep(observableChunk);

      // Do the fetch
      const promise = new Promise(function(resolve, reject) {
        const sanitizedObservableChunk = observableChunk.map(
            ({type, value}) => {
              // filter out any properties other than type and value that may have been attached to the object
              return {type, value};
            }
          ),
          {promise: dispositionPromise, cancel} = fetchDispositions(
            sanitizedObservableChunk
          );

        dispositionPromise
          .then(({status, data}) => {
            if (status !== 200) {
              reject(Error("Error fetching dispositions..."));
              return;
            }

            clonedObservableChunk.forEach(obs => {
              Object.assign(obs, processDispositionData(data, obs));
              obs.status = dispositionStatus.COMPLETED;
            });

            _this.addObservables(clonedObservableChunk);

            // return the data for async resolution
            resolve(clonedObservableChunk);

            if (fromQueue) {
              _this.currentConnections -= 1;
            }
          })
          .catch(e => {
            console.warn(
              "Disposition lookup failed for chunk:",
              observableChunk,
              e
            );

            observableChunk.forEach(observable => {
              const key = observableKey(observable);

              // Remove from the pending queue
              delete _this.pending[key];
            });

            // Update cache
            clonedObservableChunk.forEach(obs => {
              obs.status = dispositionStatus.FAILED;
            });

            _this.addObservables(clonedObservableChunk);

            if (fromQueue) {
              _this.currentConnections -= 1;
            }
          });

        if (dispositionPromise) {
          dispositionPromises.push(cancel);
        }
      }).catch(e => {
        // Ignore cancels
        if (e.name === "RequestCancelled") {
          return;
        }

        console.error(e);
      });

      promises.push(promise);
    }

    // resolve the cached dispositions
    if (noFetch.length > 0) {
      promises.push(
        Promise.resolve(addDispositionFromCache(noFetch, this.cache))
      );
    }

    return {
      networkCalls: dispositionPromises,
      cancel: () => {
        dispositionPromises.forEach(c => {
          c();
        });
      },
      promise: Promise.all(promises)
        .then(dispositions => {
          return flatten(dispositions);
        })
        .catch(e => {
          console.error(e);
        })
    };
  }

  // Synchronous
  getDispositionsFromCache(observables) {
    const _observables = Array.isArray(observables)
        ? observables
        : [observables],
      cached = [];

    _observables.forEach(obs => {
      const key = observableKey(obs);

      let result = this.cache[key] || this.pending[key];

      if (result) {
        cached.push({...obs, ...result});
      }
    });

    return cached;
  }

  // Adds observables to the cache after being fetched, and
  // publishes subscription topics for them.
  addObservables(observables = []) {
    const timestamp = new Date().getTime(),
      _observables = Array.isArray(observables) ? observables : [observables];

    // Load the cache for good measure, but don't save
    // since it'll be saved at the end of this function.
    this.loadCache(false);

    // Set a timestamp since it was just fetched
    _observables.forEach(observable => {
      const key = observableKey(observable),
        {disposition, verdicts, status} = observable;

      this.cache[key] = {
        disposition,
        verdicts,
        status,
        verdictCount: verdicts && verdicts.length,
        timestamp
      };

      delete this.pending[key];

      // For optimization, publish for each observable
      Subscriptions.publish(`${updateKey}-${key}`, observable);
    });

    // For anything wanting a generic bulk update, publish root key
    Subscriptions.publish(updateKey, _observables);

    this.saveCache();
  }

  addPendingObservables(observables = []) {
    const _observables = Array.isArray(observables)
      ? observables
      : [observables];

    _observables.forEach(observable => {
      observable.status = dispositionStatus.PENDING;
      const key = observableKey(observable);
      this.pending[key] = observable;

      Subscriptions.publish(`${updateKey}-${key}`, observable);
    });

    Subscriptions.publish(updateKey, _observables);
  }

  updateObservables(observables = []) {
    const _observables = Array.isArray(observables)
      ? observables
      : [observables];

    _observables.forEach(observable => {
      const key = observableKey(observable);
      delete this.cache[key];

      this.addToQueue(observable);
    });

    this.saveCache();
  }

  saveCache(cache) {
    if (!window.localStorage) {
      return;
    }

    const now = Date.now();

    // https://github.com/threatgrid/pabst-components/issues/244
    try {
      window.localStorage.setItem(
        cacheKey,
        JSON.stringify(cache || this.cache)
      );
      window.localStorage.setItem(timestampKey, now);

      this.cacheTime = now;
    } catch (e) {
      console.error(e);
    }
  }

  mergeObservables(original, updated) {
    return mergeObservables(original, updated);
  }

  addToQueue(observables) {
    const _observables = Array.isArray(observables)
        ? observables
        : [observables],
      cached = {};

    _observables.forEach(o => {
      const key = observableKey(o);

      if (this.cache[key]) {
        cached[key] = cloneDeep(this.cache[key]);
      }

      // Already pending, bail
      if (this.pending[key]) {
        return;
      }

      this.queue[key] = o;
    });

    if (Object.keys(this.queue).length >= chunkSize) {
      this.reconcileQueue();
    }

    return cached;
  }

  reconcileQueue() {
    if (!Services.apiHost) {
      return;
    }

    const observables = Object.values(this.queue);

    if (observables.length) {
      this.getDispositions(observables, false, true);
    }
  }

  cached(observables) {
    const _observables = Array.isArray(observables)
        ? observables
        : [observables],
      cached = {};

    _observables.forEach(o => {
      const key = observableKey(o);

      if (this.cache[key]) {
        cached[key] = cloneDeep(this.cache[key]);
      }
    });

    return cached;
  }

  clear() {
    this.cache = {};

    this.queue = {};
    this.pending = {};

    if (window.localStorage) {
      window.localStorage.removeItem(cacheKey);
      window.localStorage.removeItem(timestampKey);
    }
  }
}

const Configuration = () => {
  if (!window.atsSecurityRibbonDispositionCache) {
    window.atsSecurityRibbonDispositionCache = new DispositionCache();
  }

  return window.atsSecurityRibbonDispositionCache;
};

export default Configuration();
